import { useTranslation } from "react-i18next"

import { ThumbsDown, ThumbsUp } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { cn } from "@/lib/utils"

import { type ResponseFeedbackFields, type Sentiment } from "../schemas"

type ResponseFeedbackStepProps = {
  initialFields: ResponseFeedbackFields
  values: ResponseFeedbackFields
  onChange: (values: ResponseFeedbackFields) => void
  onBack?: () => void
  onSubmit: () => void
  submitting: boolean
  hideBack?: boolean
}

export function ResponseFeedbackStep({
  values,
  onChange,
  onBack,
  onSubmit,
  submitting,
  hideBack,
}: ResponseFeedbackStepProps) {
  const { t } = useTranslation()

  const setSentiment = (sentiment: Sentiment) => {
    onChange({ ...values, sentiment })
  }

  const setComment = (comment: string) => {
    onChange({ ...values, comment })
  }

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <p className="text-sm font-medium">
          {t("feedback.responseFeedback.sentimentLabel", "How was the answer?")}
        </p>
        <div className="flex gap-2">
          <SentimentButton
            active={values.sentiment === "up"}
            icon={ThumbsUp}
            label={t("feedback.responseFeedback.up", "Helpful")}
            onClick={() => setSentiment("up")}
          />
          <SentimentButton
            active={values.sentiment === "down"}
            icon={ThumbsDown}
            label={t("feedback.responseFeedback.down", "Not helpful")}
            onClick={() => setSentiment("down")}
          />
        </div>
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium" htmlFor="feedback-response-comment">
          {t("feedback.responseFeedback.commentLabel", "Anything specific? (optional)")}
        </label>
        <Textarea
          className="focus-visible:border-primary focus-visible:ring-0"
          id="feedback-response-comment"
          rows={3}
          value={values.comment ?? ""}
          placeholder={
            values.sentiment === "up"
              ? t(
                  "feedback.responseFeedback.commentPlaceholderUp",
                  "What did you like? Anything we should do more of?"
                )
              : t(
                  "feedback.responseFeedback.commentPlaceholderDown",
                  "What was wrong or missing about the answer?"
                )
          }
          onChange={(e) => setComment(e.target.value)}
        />
      </div>

      <div className="flex justify-between gap-2 pt-2">
        {hideBack ? (
          <span />
        ) : (
          <Button disabled={submitting} variant="outline" onClick={onBack}>
            {t("common.back", "Back")}
          </Button>
        )}
        <Button disabled={submitting} onClick={onSubmit}>
          {t("feedback.actions.review", "Review")}
        </Button>
      </div>
    </div>
  )
}

type SentimentButtonProps = {
  active: boolean
  icon: typeof ThumbsUp
  label: string
  onClick: () => void
}

function SentimentButton({ active, icon: Icon, label, onClick }: SentimentButtonProps) {
  return (
    <button
      type="button"
      className={cn(
        "flex flex-1 items-center justify-center gap-2 rounded-md border p-3 text-sm transition-colors",
        active
          ? "border-primary bg-primary/10 text-foreground"
          : "border-border bg-background text-muted-foreground hover:bg-accent/40"
      )}
      onClick={onClick}
    >
      <Icon className="size-4" />
      {label}
    </button>
  )
}
